import { useCallback, useEffect, useRef, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import type { ItemMpMatchViewModel } from "@models/job";

// Long enough to sit out the pause between two words, short enough that a
// reviewer who stopped typing does not notice waiting.
const DEBOUNCE_MS = 400;
const LIMIT = 100;

export interface UseItemMpSearchResult {
  items: ItemMpMatchViewModel[];
  loading: boolean;
  search: (query: string) => void;
}

/**
 * Server-side autocomplete over the job plugin's full item_mp catalogue, for
 * the "add row" picker.
 *
 * The catalogue runs to a couple of thousand fields, so an empty query fetches
 * nothing: a page of arbitrary alphabetical entries is no more useful than an
 * empty dropdown, and shipping it costs a round trip on every open. Results
 * arrive once the reviewer stops typing.
 *
 * Keep this state next to the picker. Hoisting it re-renders the whole results
 * table on every keystroke, which is visible as input lag on a large job.
 */
export const useItemMpSearch = (jobId: string | undefined): UseItemMpSearchResult => {
  const [items, setItems] = useState<ItemMpMatchViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const requestId = useRef(0);

  useEffect(() => {
    const needle = query.trim();
    if (!jobId || !needle) {
      // Bump the id so a request still in flight from the previous query can't
      // land and repopulate the dropdown the reviewer just cleared.
      requestId.current += 1;
      setItems([]);
      setLoading(false);
      return;
    }
    const timer = setTimeout(() => {
      const id = ++requestId.current;
      setLoading(true);
      mappingApi
        .searchItemMp(jobId, needle, LIMIT)
        .then((found) => {
          if (id === requestId.current) setItems(found);
        })
        .catch(() => {})
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [jobId, query]);

  const search = useCallback((next: string): void => setQuery(next), []);

  return { items, loading, search };
};
